<template>
  <!-- :key on v-card is necessary for animations -->
  <v-card
    class="d-flex flex-column topic-card"
    data-testid="topics-topic-card"
    :style="cssVars"
    border
    @click="handleCardClick"
  >
    <v-card-title class="d-flex flex-nowrap align-center break-words">
      <h4
        :class="[
          'font-weight-regular text--primary flex-grow-1 text-wrap break-word ',
          {
            'line-clamp-2': isMobile,
          },
        ]"
      >
        <v-tooltip
          v-if="!isUserEditable"
          location="top"
        >
          <template v-slot:activator="{ props }">
            <span v-bind="props">
              <v-icon class="mr-2"> mdi-lock </v-icon>
            </span>
          </template>
          <span>{{ $t('topics.list.card.actions.edit-disabled') }}</span>
        </v-tooltip>

        {{ name }}

        <v-chip
          v-if="hasLandingpage"
          color="primary"
          rounded="pill"
          density="compact"
          :class="isMobile ? '' : 'ml-4'"
          variant="flat"
          size="small"
          height="20"
        >
          <span class="text-white">
            {{ $t('topics.list.card.actions.landingpage-badge') }}
          </span>
        </v-chip>
      </h4>

      <v-spacer />

      <Permission permission="topic:write">
        <div class="button-group">
          <v-tooltip location="top">
            <template v-slot:activator="{ props }">
              <span v-bind="props">
                <v-btn
                  icon
                  color="primary"
                  variant="text"
                  class="btn--fullscreen"
                  data-product-fruits="topics-edit"
                  data-testid="topics-edit-topic"
                  @click.stop="emitEdit"
                >
                  <v-icon>mdi-fullscreen</v-icon>
                </v-btn>
              </span>
            </template>
            <span>{{
              isUserEditable
                ? $t('topics.list.card.actions.edit')
                : $t('topics.list.card.actions.show')
            }}</span>
          </v-tooltip>
        </div>
      </Permission>
    </v-card-title>

    <v-divider />

    <v-card-text class="content text-body-1">
      <div class="description">
        <span
          v-if="description"
          class="line-breaks line-clamp"
        >
          {{ description }}
        </span>
        <span
          v-else-if="isArchived"
          class="text--secondary"
        >
          {{ $t('topics.list.card.no-description-archived') }}
        </span>
        <span
          v-else
          class="text--secondary font-italic"
        >
          {{ $t('topics.list.card.no-description') }}
        </span>
      </div>
    </v-card-text>

    <v-card-actions class="card-actions">
      <v-btn
        v-if="canCreateContent"
        variant="text"
        color="primary"
        class="ml-2 content-creator-btn"
        data-product-fruits="topics-content-creator"
        :to="{ name: 'content-creator', query: { topicId: id, section: 'creator' } }"
        @click.stop="$tracking.event('Marketing Topics', 'Clicked', 'Create Content')"
      >
        <v-icon start> mdi-brain </v-icon>
        {{ $t('topics.list.card.actions.content-creator') }}
      </v-btn>
      <Feature :feature-slug="featureNames.AD_MATERIALS">
        <v-btn
          variant="text"
          color="primary"
          class="ml-2 ad-materials-btn"
          data-product-fruits="topics-ad-materials"
          :to="{ name: 'ad-materials', query: { topicIds: id } }"
          @click.stop="$tracking.event('Marketing Topics', 'Clicked', 'View Ad Materials')"
        >
          <v-icon start> mdi-image-multiple </v-icon>
          {{ $t('topics.list.card.actions.ad-materials') }}
        </v-btn>
      </Feature>
      <v-btn
        v-if="hasLandingpage"
        variant="text"
        class="landingpage-details-btn"
        :to="{ name: 'landingpage-details', params: { slug: landingpage.slug } }"
        @click.stop="$tracking.event('Marketing Topics', 'Clicked', 'Widget')"
      >
        <v-icon start> mdi-button-pointer </v-icon>
        {{ $t('topics.list.card.actions.landingpage-detail') }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import Permission from '@/components/Permission'
import featureMixin from '@/mixins/feature'
import performingActionMixin from '@/mixins/performingAction'

import TopicStatus from './TopicStatus'

export default {
  components: {
    Permission,
  },

  mixins: [performingActionMixin, featureMixin],

  props: {
    id: {
      type: String,
      default: '',
    },
    name: {
      type: String,
      default: '',
    },
    description: {
      type: String,
      default: '',
    },
    status: {
      type: String,
      default: '',
    },
    isSystemGenerated: {
      type: Boolean,
      default: false,
    },
    isUserEditable: {
      type: Boolean,
      default: true,
    },
    landingpage: {
      type: Object,
      default: null,
    },
  },

  data: () => ({}),

  computed: {
    cssVars() {
      return {
        '--border-color': this.$vuetify.theme.global?.current?.colors['markero-blue'],
      }
    },
    isArchived() {
      return this.status === TopicStatus.ARCHIVED
    },

    hasLandingpage() {
      return Boolean(this.landingpage?.id)
    },

    isMobile() {
      return this.$vuetify.display.smAndDown
    },
    canCreateContent() {
      return !this.isArchived
    },
  },

  methods: {
    handleCardClick() {
      this.emitEdit()
    },

    emitEdit() {
      this.$emit('edit', {
        id: this.id,
        name: this.name,
        description: this.description,
        status: this.status,
        isSystemGenerated: this.isSystemGenerated,
        isUserEditable: this.isUserEditable,
        landingpage: this.landingpage,
      })
    },
  },
  emits: ['edit'],
}
</script>

<style scoped>
@media (max-width: 600px) {
  .card-actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
  }

  .content-creator-btn {
    margin: 0 auto !important;
  }

  .landingpage-details-btn {
    order: 3;
    margin: 0 auto !important;
  }

  .content-creator-btn {
    order: 4;
  }

  .ad-materials-btn {
    order: 5;
    margin: 0 auto !important;
  }

  .btn--fullscreen {
    margin-right: -8px !important;
  }
}

.button-group {
  display: flex;
  justify-content: flex-end;
  min-width: 100px;
}

.clickable {
  cursor: pointer;
  transition: box-shadow 0.2s;
}

.clickable:hover {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.description {
  max-width: 800px;
}

.break-words {
  word-break: break-word;
}

.line-breaks {
  white-space: pre-line;
}
.line-clamp {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
